import { APPLICATION_TYPE_CODE } from '../list/constants'

import type { StaffManualApplicationFormData } from './types'

export const STEP_ITEMS = [
  { key: 'service', label: 'Service', shortLabel: 'Service' },
  { key: 'personal', label: 'Personal information', shortLabel: 'Personal' },
  { key: 'current-address', label: 'Current address', shortLabel: 'Current' },
  { key: 'mailing-address', label: 'Mailing address', shortLabel: 'Mailing' },
  { key: 'contact', label: 'Contact information', shortLabel: 'Contact' },
  { key: 'referral', label: 'How did you hear us?', shortLabel: 'Referral' },
  { key: 'disclaimers', label: 'Disclaimers', shortLabel: 'Terms' },
] as const

export const MAILING_DESTINATION_OPTIONS = {
  USCanada: 'USCanadaAddress',
  Frontier: 'FrontierForwarding',
} as const

export type TMailingDestination =
  (typeof MAILING_DESTINATION_OPTIONS)[keyof typeof MAILING_DESTINATION_OPTIONS]

export const MAILING_ADDRESS_OPTIONS = {
  Current: 'CurrentAddress',
  Other: 'OtherAddress',
} as const

export type TMailingAddressOption =
  (typeof MAILING_ADDRESS_OPTIONS)[keyof typeof MAILING_ADDRESS_OPTIONS]

export const MAILING_ADDRESS_OPTION_LABELS: Record<string, string> = {
  [MAILING_ADDRESS_OPTIONS.Current]: 'Current Address',
  [MAILING_ADDRESS_OPTIONS.Other]: 'Other Address',
}

export const MAILING_COURIER_OPTION_LABELS: Record<string, string> = {
  CanadaPostRegistered: "Canada Post's Registered Mail",
  PrepaidCourierLabel: 'DHL / UPS / FedEx',
}

export const MAILING_DESTINATION_LABELS = {
  [MAILING_DESTINATION_OPTIONS.USCanada]:
    'Canadian/US Address (The RCMP will mail it directly to your address)',
  [MAILING_DESTINATION_OPTIONS.Frontier]:
    'Foreign Country (Frontier One will receive it and mail it to you for an extra fee)',
} as const

export const MAILING_DESTINATION_LABEL_SHORT = {
  [MAILING_DESTINATION_OPTIONS.USCanada]: 'Canadian/US Address',
  [MAILING_DESTINATION_OPTIONS.Frontier]: 'Foreign Country',
}

export const MAILING_DESTINATION_SELECT_OPTIONS = Object.entries(MAILING_DESTINATION_LABEL_SHORT).map(([key, value]) => ({
  value: key,
  label: value,
}))

export const MAILING_OPTION_OPTIONS = {
  CanadaPost: 'canadaPost',
  Courier: 'courier',
}

export type TMailingOption = (typeof MAILING_OPTION_OPTIONS)[keyof typeof MAILING_OPTION_OPTIONS]

export const SERVICE_REASON_OPTIONS = [
  {
    value: APPLICATION_TYPE_CODE.PRIVACY_ACT,
    label: 'CRIMINAL RECORD CHECK FOR PERSONAL USE (CMP-PPU-030)',
  },
  {
    value: APPLICATION_TYPE_CODE.PERMANENT_RESIDENT,
    label: 'PERMANENT RESIDENT STATUS',
  },
  {
    value: APPLICATION_TYPE_CODE.CANADIAN_CITIZENSHIP,
    label: 'CANADIAN CITIZENSHIP',
  },
  {
    value: APPLICATION_TYPE_CODE.VISA_BORDER_CROSSING_FOREIGN_TRAVEL_WORK,
    label: 'VISA/BORDER CROSSING/FOREIGN TRAVEL/WORK',
  },
  {
    value: APPLICATION_TYPE_CODE.OTHERS,
    label: 'OTHERS',
  },
] as const

export const REFERRAL_OPTIONS = [
  { value: 'Returning Client', label: 'Returning Client' },
  { value: 'Google Search', label: 'Google Search' },
  { value: 'Google Maps', label: 'Google Maps' },
  { value: 'Friends and Family', label: 'Friends and Family' },
  {
    value: 'Private Agencies (e.g., Immigration Consultant, Employment Agency, Employer, etc.)',
    label: 'Private Agencies (e.g., Immigration Consultant, Employment Agency, Employer, etc.)',
  },
  {
    value: 'Government Institutions (e.g., Police, Government Department, etc.)',
    label: 'Government Institutions (e.g., Police, Government Department, etc.)',
  },
  { value: 'Facebook, Instagram, Twitter, LinkedIn, ...', label: 'Facebook, Instagram, Twitter, LinkedIn, ...' },
  { value: 'Online Forums or Communities', label: 'Online Forums or Communities' },
] as const

export const STAFF_MANUAL_APPLICATION_FORM_STORAGE_KEY = 'fingerprinting-create-form'

export const STAFF_MANUAL_APPLICATION_MAX_STEP_INDEX = STEP_ITEMS.length - 1

const defaultLineAddress = (): StaffManualApplicationFormData['currentAddress'] => ({
  country: '',
  unit: '',
  street: '',
  city: '',
  state: '',
  zip: '',
})

export const DEFAULT_STAFF_MANUAL_APPLICATION_FORM_DATA: StaffManualApplicationFormData = {
  selectedReason: '',
  otherReasonNote: '',
  reason: '',
  apostille: false,
  apostilleCountry: '',
  personalInfo: {
    lastName: '',
    firstName: '',
    middleName: '',
    maidenName: '',
    gender: '',
    dob: '',
    citizenship: '',
  },
  currentAddress: defaultLineAddress(),
  mailingAddress: {
    destination: MAILING_DESTINATION_OPTIONS.USCanada,
    mailingAddressOption: MAILING_ADDRESS_OPTIONS.Current,
    mailingOption: 'courier',
    other: defaultLineAddress(),
  },
  contactInfo: {
    email: '',
    confirmEmail: '',
    phoneCode: 'CA',
    phoneNumber: '',
  },
  referral: {
    referral: '',
    referralOther: '',
  },
  agreedTerms: false,
}

